import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useLazyQuery, gql } from '@apollo/client';
import { HardDrive, Save, Plug, AlertCircle, Cloud, CheckCircle2, Unplug } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const GET_STORAGE_PROVIDER_INFO = gql`
  query GetStorageProviderInfo {
    storageProviderInfo {
      providerType
      isActive
      connectedAt
    }
  }
`;

const GET_COMPANY_STORAGE_SETTINGS = gql`
  query GetCompanyStorageSettings {
    companyStorageSettings {
      driveRootFolderName
      maxFileSizeMB
      allowedMimeTypes
      totalQuotaGB
      usedBytes
    }
  }
`;

const GET_STORAGE_CAPABILITIES = gql`
  query GetStorageCapabilities {
    storageCapabilities {
      internalStorageEnabled
    }
  }
`;

const CONFIGURE_S3_STORAGE = gql`
  mutation ConfigureS3Storage($input: S3CompatibleConfigInput!) {
    configureS3Storage(input: $input)
  }
`;

const GOOGLE_DRIVE_AUTH_URL = gql`
  query GoogleDriveAuthUrl {
    googleDriveAuthUrl
  }
`;

const DEACTIVATE_STORAGE_PROVIDER = gql`
  mutation DeactivateStorageProvider {
    deactivateStorageProvider
  }
`;

const UPDATE_COMPANY_STORAGE_SETTINGS = gql`
  mutation UpdateCompanyStorageSettings($input: UpdateStorageSettingsInput!) {
    updateCompanyStorageSettings(input: $input) {
      driveRootFolderName
      maxFileSizeMB
      allowedMimeTypes
      totalQuotaGB
      usedBytes
    }
  }
`;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const StorageSettingsCard: React.FC = () => {
  const { data: providerData, refetch: refetchProvider } = useQuery<{
    storageProviderInfo: { providerType?: string; isActive?: boolean; connectedAt?: string } | null;
  }>(GET_STORAGE_PROVIDER_INFO, { fetchPolicy: 'network-only' });

  const { data: settingsData, refetch: refetchSettings } = useQuery<{
    companyStorageSettings: {
      driveRootFolderName: string | null;
      maxFileSizeMB: number;
      allowedMimeTypes: string[];
      totalQuotaGB: number;
      usedBytes: number;
    };
  }>(GET_COMPANY_STORAGE_SETTINGS, { fetchPolicy: 'network-only' });

  const { data: capabilitiesData } = useQuery<{
    storageCapabilities: { internalStorageEnabled: boolean };
  }>(GET_STORAGE_CAPABILITIES, { fetchPolicy: 'network-only' });

  // Nasz system załączników (S3-compatible) jest dostępny tylko dla nas / w płatnym dodatku.
  // Gdy wyłączony, klient (np. SZKŁO) widzi wyłącznie Google Drive.
  const internalStorageEnabled =
    capabilitiesData?.storageCapabilities?.internalStorageEnabled ?? false;

  const [configureS3] = useMutation(CONFIGURE_S3_STORAGE);
  const [deactivateProvider] = useMutation(DEACTIVATE_STORAGE_PROVIDER);
  const [updateSettings, { loading: savingSettings }] = useMutation(
    UPDATE_COMPANY_STORAGE_SETTINGS,
  );

  const [fetchGoogleDriveAuthUrl, { loading: loadingDriveAuth }] = useLazyQuery<{
    googleDriveAuthUrl: string;
  }>(GOOGLE_DRIVE_AUTH_URL, { fetchPolicy: 'network-only' });

  // Provider tab — Google Drive is the default (and, for most clients, the only) option.
  const [providerTab, setProviderTab] = useState<'S3_COMPATIBLE' | 'GOOGLE_DRIVE'>(
    'GOOGLE_DRIVE',
  );

  // S3 form
  const [s3Endpoint, setS3Endpoint] = useState('');
  const [s3Region, setS3Region] = useState('us-east-1');
  const [s3Bucket, setS3Bucket] = useState('');
  const [s3AccessKey, setS3AccessKey] = useState('');
  const [s3SecretKey, setS3SecretKey] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);

  // Settings form
  const [folderName, setFolderName] = useState('');
  const [maxFileSizeMB, setMaxFileSizeMB] = useState(10);
  const [totalQuotaGB, setTotalQuotaGB] = useState(5);
  const [allowedMimeTypesText, setAllowedMimeTypesText] = useState('');

  useEffect(() => {
    const s = settingsData?.companyStorageSettings;
    if (s) {
      setFolderName(s.driveRootFolderName ?? '');
      setMaxFileSizeMB(s.maxFileSizeMB);
      setTotalQuotaGB(s.totalQuotaGB);
      setAllowedMimeTypesText(s.allowedMimeTypes.join('\n'));
    }
  }, [settingsData]);

  const provider = providerData?.storageProviderInfo;
  const isConnected = !!provider?.providerType;

  // Default the selected tab to the currently connected provider (once known).
  // When our internal storage is disabled, stay on Google Drive regardless.
  useEffect(() => {
    if (!internalStorageEnabled) {
      setProviderTab('GOOGLE_DRIVE');
      return;
    }
    if (provider?.providerType === 'GOOGLE_DRIVE' || provider?.providerType === 'S3_COMPATIBLE') {
      setProviderTab(provider.providerType);
    }
  }, [provider?.providerType, internalStorageEnabled]);

  // Handle the OAuth redirect back from Google.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const storage = params.get('storage');
    if (!storage) return;

    if (storage === 'connected') {
      toast.success('Połączono z Google Drive');
      refetchProvider();
    } else if (storage === 'error') {
      const reason = params.get('reason');
      const reasonMessages: Record<string, string> = {
        no_refresh_token:
          'Google nie zwrócił tokenu odświeżania — spróbuj ponownie i zatwierdź dostęp.',
        state: 'Link wygasł, spróbuj ponownie.',
      };
      const detail = reason ? reasonMessages[reason] ?? reason : '';
      toast.error('Nie udało się połączyć z Google Drive' + (detail ? ': ' + detail : ''));
    }

    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await deactivateProvider();
      toast.success('Rozłączono z Google Drive');
      setConfirmDisconnect(false);
      await refetchProvider();
    } catch (e: any) {
      toast.error(e.message || 'Nie udało się rozłączyć');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleConnectGoogleDrive = async () => {
    try {
      const { data } = await fetchGoogleDriveAuthUrl();
      const url = data?.googleDriveAuthUrl;
      if (!url) {
        toast.error('Nie udało się pobrać linku do logowania Google');
        return;
      }
      window.location.href = url;
    } catch (e: any) {
      toast.error(e.message || 'Nie udało się rozpocząć łączenia z Google Drive');
    }
  };

  const driveConnected = provider?.providerType === 'GOOGLE_DRIVE' && !!provider?.isActive;

  const handleSaveS3 = async () => {
    if (!s3Bucket || !s3AccessKey || !s3SecretKey) {
      toast.error('Bucket, accessKeyId i secretAccessKey są wymagane');
      return;
    }
    setSavingProvider(true);
    try {
      await configureS3({
        variables: {
          input: {
            endpoint: s3Endpoint || null,
            region: s3Region,
            bucket: s3Bucket,
            accessKeyId: s3AccessKey,
            secretAccessKey: s3SecretKey,
            forcePathStyle: !!s3Endpoint,
          },
        },
      });
      toast.success('Konfiguracja S3 zapisana');
      setS3SecretKey('');
      await refetchProvider();
    } catch (e: any) {
      toast.error(e.message || 'Błąd zapisu konfiguracji');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleSaveSettings = async () => {
    const mimeTypes = allowedMimeTypesText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      await updateSettings({
        variables: {
          input: {
            driveRootFolderName: folderName.trim(),
            maxFileSizeMB,
            totalQuotaGB,
            allowedMimeTypes: mimeTypes,
          },
        },
      });
      toast.success('Ustawienia pamięci zapisane');
      await refetchSettings();
    } catch (e: any) {
      toast.error(e.message || 'Błąd zapisu limitów');
    }
  };

  const usage = settingsData?.companyStorageSettings;
  const usedBytes = usage?.usedBytes ?? 0;
  const quotaBytes = (usage?.totalQuotaGB ?? 0) * 1024 * 1024 * 1024;
  const usagePct = quotaBytes > 0 ? Math.round((usedBytes / quotaBytes) * 100) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="w-5 h-5" />
          Pamięć i załączniki
        </CardTitle>
        <CardDescription>
          {internalStorageEnabled
            ? 'Skonfiguruj dostawcę pamięci dla plików załączników (Dysk Google albo nasz system załączników) oraz limity per firma.'
            : 'Podłącz Dysk Google jako miejsce na pliki załączników oraz ustaw limity per firma.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Provider status */}
        <div className="rounded-md border p-3 space-y-1 bg-muted/30">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Plug className="w-4 h-4" />
            Status dostawcy
          </div>
          {isConnected ? (
            <div className="text-sm text-muted-foreground">
              <strong>
                {provider!.providerType === 'GOOGLE_DRIVE'
                  ? 'Google Drive'
                  : 'Nasz system załączników'}
              </strong> — {provider!.isActive ? 'aktywny' : 'nieaktywny'}
              {provider!.connectedAt && (
                <> · podłączono {new Date(provider!.connectedAt).toLocaleString('pl-PL')}</>
              )}
            </div>
          ) : (
            <div className="text-sm text-amber-700 dark:text-amber-500 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Brak skonfigurowanego dostawcy — załączniki nie będą działać
            </div>
          )}
        </div>

        {/* Provider type selector — only when our internal storage add-on is enabled */}
        {internalStorageEnabled && (
          <div className="inline-flex rounded-md border p-1 bg-muted/30">
            <button
              type="button"
              onClick={() => setProviderTab('GOOGLE_DRIVE')}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                providerTab === 'GOOGLE_DRIVE'
                  ? 'bg-background text-foreground shadow-sm font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Google Drive
            </button>
            <button
              type="button"
              onClick={() => setProviderTab('S3_COMPATIBLE')}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                providerTab === 'S3_COMPATIBLE'
                  ? 'bg-background text-foreground shadow-sm font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Nasz system załączników
            </button>
          </div>
        )}

        {/* Google Drive section */}
        {providerTab === 'GOOGLE_DRIVE' && (
          <div className="space-y-3">
            <div className="text-sm font-medium">Połączenie z Google Drive</div>
            <p className="text-sm text-muted-foreground">
              Pliki będą zapisywane na firmowym Dysku Google, w folderze
              „Odbij – Załączniki”, z automatycznym podziałem na projekty i zadania.
              Połączenie odbywa się jednym kliknięciem — nie musisz nigdzie ręcznie
              wpisywać danych logowania.
            </p>

            {driveConnected ? (
              <div className="rounded-md border p-3 space-y-2 bg-muted/30">
                <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  Połączono z Google Drive
                </div>
                {provider?.connectedAt && (
                  <div className="text-sm text-muted-foreground">
                    Podłączono {new Date(provider.connectedAt).toLocaleString('pl-PL')}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleConnectGoogleDrive}
                    disabled={loadingDriveAuth}
                    className="gap-2"
                  >
                    <Cloud className="w-4 h-4" />
                    {loadingDriveAuth ? 'Przekierowywanie...' : 'Połącz ponownie'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmDisconnect(true)}
                    disabled={disconnecting}
                    className="gap-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                  >
                    <Unplug className="w-4 h-4" />
                    Rozłącz
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={handleConnectGoogleDrive}
                disabled={loadingDriveAuth}
                className="gap-2"
              >
                <Cloud className="w-4 h-4" />
                {loadingDriveAuth ? 'Przekierowywanie...' : 'Połącz z Google Drive'}
              </Button>
            )}
          </div>
        )}

        {/* S3 form — internal storage add-on, only when enabled */}
        {internalStorageEnabled && providerTab === 'S3_COMPATIBLE' && (
        <div className="space-y-3">
          <div className="text-sm font-medium">Konfiguracja naszego systemu załączników</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Endpoint (opcjonalnie, np. https://&lt;account&gt;.r2.cloudflarestorage.com)</Label>
              <Input
                value={s3Endpoint}
                onChange={(e) => setS3Endpoint(e.target.value)}
                placeholder="https://...  (zostaw puste dla AWS S3)"
              />
            </div>
            <div>
              <Label>Region</Label>
              <Input value={s3Region} onChange={(e) => setS3Region(e.target.value)} />
            </div>
            <div>
              <Label>Bucket</Label>
              <Input
                value={s3Bucket}
                onChange={(e) => setS3Bucket(e.target.value)}
                placeholder="workflow-attachments"
              />
            </div>
            <div>
              <Label>Access Key ID</Label>
              <Input value={s3AccessKey} onChange={(e) => setS3AccessKey(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Secret Access Key</Label>
              <Input
                type="password"
                value={s3SecretKey}
                onChange={(e) => setS3SecretKey(e.target.value)}
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Po zapisaniu klucz jest szyfrowany w bazie i nigdy nie zwracany do interfejsu.
                Wpisz go ponownie, żeby zaktualizować.
              </p>
            </div>
          </div>
          <Button onClick={handleSaveS3} disabled={savingProvider} className="gap-2">
            <Save className="w-4 h-4" />
            {savingProvider ? 'Zapisywanie...' : 'Zapisz konfigurację S3'}
          </Button>
        </div>
        )}

        {/* Limits form */}
        <div className="space-y-3 border-t pt-4">
          <div className="text-sm font-medium">Ustawienia firmy</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nazwa folderu na Dysku Google</Label>
              <Input
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Odbij – Załączniki"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Folder tworzony na Dysku przy łączeniu konta. Zostaw puste, aby
                użyć domyślnej nazwy „Odbij – Załączniki". Zmiana przy już
                połączonym koncie zmieni nazwę istniejącego folderu.
              </p>
            </div>
            <div>
              <Label>Maks. rozmiar pojedynczego pliku (MB)</Label>
              <Input
                type="number"
                min={1}
                value={maxFileSizeMB}
                onChange={(e) => setMaxFileSizeMB(Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Łączna pojemność (GB)</Label>
              <Input
                type="number"
                min={1}
                value={totalQuotaGB}
                onChange={(e) => setTotalQuotaGB(Number(e.target.value))}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Dozwolone typy MIME (jeden na linię lub po przecinku)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={allowedMimeTypesText}
                onChange={(e) => setAllowedMimeTypesText(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={savingSettings} className="gap-2">
            <Save className="w-4 h-4" />
            {savingSettings ? 'Zapisywanie...' : 'Zapisz ustawienia'}
          </Button>
        </div>

        {/* Usage */}
        {usage && (
          <div className="rounded-md border p-3 space-y-1 bg-muted/30 text-sm">
            <div className="font-medium">Zużycie</div>
            <div className="text-muted-foreground">
              {formatBytes(usedBytes)} z {usage.totalQuotaGB} GB ({usagePct}%)
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${usagePct >= 80 ? 'bg-red-600' : 'bg-primary'}`}
                style={{ width: `${Math.min(100, usagePct)}%` }}
              />
            </div>
          </div>
        )}

        <Dialog
          open={confirmDisconnect}
          onOpenChange={(v) => {
            if (!v) setConfirmDisconnect(false);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rozłączyć Google Drive?</DialogTitle>
              <DialogDescription>
                Aplikacja przestanie zapisywać nowe załączniki na tym koncie
                Google. Folder i pliki już wgrane na Dysk <strong>NIE</strong>{' '}
                zostaną usunięte — pozostaną na Google Drive. Będzie można
                połączyć ponownie (także innym kontem).
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDisconnect(false)}
                disabled={disconnecting}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? 'Rozłączanie...' : 'Rozłącz'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
